import React from 'react';

import { Container, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { scroller } from 'react-scroll';

import FilledClockIcon from '~/assets/icons/filledClockIcon';
import HeaderWithExpert from '~/blocks/HeaderWithExpert';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import { DEFAULT_DISCOVER_URL } from '~/const';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { getCategoriesArray } from '~/pages/DiscoverPage/components/Hit/Hit';
import { HITS_TYPES } from '~/pages/const';
import { DISCOVER_INSIGHTS_ROUTE } from '~/routes';
import { BIG_MARGIN_PX, MIDDLE_MARGIN_PX, SMALL_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';

const NewsHeader = ({ expert, title, loading, description, minToRead, categories }) => {
  const { t, i18n } = useTenantTranslation();
  const theme = useTheme();

  const { tenant } = useTenantContext();
  const { suiteIDs } = useUserInfoContext();

  const tenantIDs = typeof tenant?.id === 'string' ? [tenant.id] : [];

  const categoriesToRender = getCategoriesArray({
    item: {
      id: expert?.id,
      title,
      categories_array: categories,
      searchable_type: HITS_TYPES.VIDEO,
    },
    isGridHit: false,
    locale: i18n.language,
    fullWidth: true,
    availableIDs: suiteIDs.length > 0 ? suiteIDs : tenantIDs,
  });

  const crumbs = [
    {
      title: t('page.discover.discover'),
      url: DEFAULT_DISCOVER_URL,
    },
    {
      title: t('page.discover.insights'),
      url: DISCOVER_INSIGHTS_ROUTE,
    },
    {
      title,
    },
  ];

  if (loading) {
    return (
      <>
        <StyledContainer>
          <Breadcrumbs loading crumbs={crumbs} />
        </StyledContainer>
        <StyledHeader loading />
        <RestrictedContainer>
          <LoadingDescription xs="35px" />
          <LoadingDescription xs="35px" />
          <LoadingDescription xs="35px" />
        </RestrictedContainer>
      </>
    );
  }

  const handleClickExpert = () => {
    scroller.scrollTo('expertSection', {
      duration: 1000,
      delay: 150,
      smooth: 'easeInOutQuart',
      offset: -150,
    });
  };

  return (
    <>
      <StyledContainer>
        <Breadcrumbs crumbs={crumbs} />
      </StyledContainer>
      <RestrictedContainer>
        {categoriesToRender}
        <LabelToRead data-testid="timeToRead">
          <StyledToReadIcon color={theme.palette.common.violet} as={FilledClockIcon} />
          {t('page.news.minToRead', { count: minToRead })}
        </LabelToRead>
      </RestrictedContainer>
      <StyledHeader user={expert} title={title} handleClickExpert={handleClickExpert} shareBlock />
      <RestrictedContainer>
        <Description>{description}</Description>
      </RestrictedContainer>
    </>
  );
};

export default NewsHeader;

const StyledHeader = styled(HeaderWithExpert)`
  margin-top: ${MIDDLE_MARGIN_PX};
  margin-bottom: ${MIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 798px;
    margin-bottom: ${BIG_MARGIN_PX};
  }
`;
const Description = styled(Typography)`
  width: 100%;
  padding-bottom: ${BIG_MARGIN_PX};
  font-size: 18px;
  line-height: 1.3;
  text-align: left;
  color: ${({ theme }) => theme.palette.common.gray};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 20px;
  }
`;
const LoadingDescription = styled(AnimatedSkeleton)`
  margin-bottom: ${TINY_MARGIN_PX};
`;
const RestrictedContainer = styled(Container)`
  z-index: 5;
  display: flex;
  gap: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 798px;
  }
`;
const LabelToRead = styled('div')`
  background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  color: ${({ theme }) => theme.palette.common.violet};
  padding: 6px 16px 6px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  width: fit-content;
`;
const StyledContainer = styled(RestrictedContainer)`
  padding-top: 92px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-top: 172px;
  }
`;
const StyledToReadIcon = styled('svg')`
  width: 16px;
  height: 16px;
  margin-right: ${TINY_MARGIN_PX};
`;
